import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane,
  faRobot,
  faSpinner,
  faUserAstronaut,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import axiosInstance from "../utils/axiosInstance";
import GetUser from "../utils/getUser";
import { toast } from "react-toastify";
import { ParseErrors } from "../utils/ParseErrors";
import ModalPortal from "../components/modal-portal";

interface INotesModel {
  onToggle: () => void;
  data: {
    policyNumber: string;
  };
}

interface Message {
  branch: string;
  clientNumber: number;
  policyLine: number;
  policyReference: string;
  createdOn: string;
  agent: {
    name: string;
    code: string;
  };
  notes: string;
}

const NotesModal = ({ onToggle, data }: INotesModel) => {
  const user = GetUser();
  const bottomRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState<{
    totalCount: number;
    data: Message[];
  }>({ totalCount: 0, data: [] });
  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [newMessage, setNewMessage] = useState("");

  //  ------------------------------------------------------------------------------------ HANDLE SEND MESSAGE -------------------------------------------------------------------------------------

  const handleSendMessage = async () => {
    if (newMessage.trim()) {
      setSending(true);
      try {
        const res = await axiosInstance.post(`/notes/${data?.policyNumber}`, {
          notes: newMessage,
        });
        setMessages((prev) => ({
          totalCount: prev.totalCount + 1,
          data: [...prev.data, res.data.data],
        }));
        setNewMessage("");
        setSending(false);
      } catch (error) {
        console.log(error);
        setSending(false);
      }
    }
  };

  //  ------------------------------------------------------------------------------------ FIND NOTES -------------------------------------------------------------------------------------

  useEffect(() => {
    const findNotes = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(
          `/notes/${data?.policyNumber}?page=${page}&limit=${limit}`
        );
        setMessages(res.data);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    data?.policyNumber && !messages.totalCount && findNotes();
  }, [data?.policyNumber]);

  // ------------------------------------------------------------------------------------ CARP NEW NOTES ------------------------------------------------------------------------------------
  const carpNewMessages = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/notes/${data?.policyNumber}?page=${page + limit}&limit=${limit}`
      );
      setPage(page + limit);
      setMessages((prev) => ({
        totalCount: res.data.totalCount,
        data: [...res.data.data, ...prev.data],
      }));
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      toast.error(ParseErrors(error));
    }
  };

  // -------------------------------------------------- SCROLL TO BOTTOM ----------------------------------------------------------------

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.totalCount]);

  return (
    <ModalPortal>
    <div className="w-full h-full fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99999] animate-fade-in p-3 sm:p-4" onClick={onToggle}>
      <div className="w-full bg-surface border border-edge max-w-[600px] h-[90vh] sm:h-[80vh] flex flex-col rounded-2xl shadow-overlay overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/*---------------------------------------------------------------------------------- Notes Header -------------------------------------------------------------------------------------*/}
        <div className="w-full p-4 flex bg-surface-muted justify-between items-center border-b border-edge">
          <h3 className="text-text-primary uppercase text-md font-bold tracking-wide">
            Policy Notes
          </h3>
          <button
            onClick={onToggle}
            className="bg-gradient-to-r from-accent to-blue flex items-center justify-center gap-2 text-sm py-2 px-4 font-normal text-md rounded-lg text-white shadow-lg shadow-indigo-500/25 hover:opacity-90 transition-opacity"
          >
            <FontAwesomeIcon icon={faXmark} size="1x" />
          </button>
        </div>

        {/*---------------------------------------------------------------------------------- Messages -------------------------------------------------------------------------------------*/}
        <div className="w-full flex h-full overflow-auto relative">
          {loading && !messages?.totalCount ? (
            <div className="w-full flex items-center justify-center py-2 absolute top-0 left-0 h-full bg-surface">
              <FontAwesomeIcon icon={faSpinner} spin size="6x" />
            </div>
          ) : null}
          {messages.data.length ? (
            <div className="w-[95%] mx-auto p-2">
              {page <= messages.totalCount ? (
                <button
                  className="text-xs text-blue justify-center items-center flex my-4 cursor-pointer mx-auto hover:text-indigo-300 transition-colors"
                  onClick={carpNewMessages}
                  disabled={loading}
                >
                  Load Recent Messages{" "}
                  {loading ? (
                    <FontAwesomeIcon icon={faSpinner} spin className="ml-2" />
                  ) : null}
                </button>
              ) : null}
              {messages.data.map((msg, index) => {
                let isSender =
                  msg.agent.code === user.code || msg.agent.code === "DS";
                return (
                  <div key={index} className="mb-6">
                    {!isSender ? (
                      <span
                        className={clsx(
                          "text-xs flex text-light-gray justify-start ml-[52px] mb-[-2px]"
                        )}
                      >
                        {msg?.agent?.name}
                      </span>
                    ) : null}
                    <div
                      className={clsx(
                        "message_box flex justify-start items-end mb-2",
                        isSender && "justify-end"
                      )}
                    >
                      {!isSender && (
                        <div className="w-10 h-10 rounded-full mr-3 bg-surface-elevated border border-edge flex items-center justify-center shrink-0">
                          <FontAwesomeIcon
                            icon={faRobot}
                            className="text-indigo-600 dark:text-indigo-300 text-sm"
                          />
                        </div>
                      )}
                      <div
                        className={clsx(
                          "py-3 text-sm bg-surface-elevated border border-edge rounded-xl text-text-primary",
                          isSender &&
                            "!bg-gradient-to-r !from-accent !to-blue !border-transparent py-1 rounded-xl !text-white px-8"
                        )}
                      >
                        <div
                          className="space-y-2"
                          dangerouslySetInnerHTML={{ __html: msg.notes }}
                        ></div>
                      </div>
                    </div>
                    <span
                      className={clsx(
                        "text-xs flex text-light-gray justify-start ml-[54px] mt-[-12px]",
                        isSender && "justify-end mt-[6px]"
                      )}
                    >
                      {moment(msg.createdOn).format("YYYY-MM-DD")}
                    </span>
                  </div>
                );
              })}
              <div ref={bottomRef}></div>
            </div>
          ) : null}
        </div>

        {/*---------------------------------------------------------------------------------- Input Field -------------------------------------------------------------------------------------*/}
        <div className="p-3 sm:p-4 bg-surface-muted border-t border-edge flex items-center gap-2 sm:gap-3">
          <div className="h-10 w-10 rounded-full bg-surface-elevated border border-edge flex items-center justify-center shrink-0">
            <FontAwesomeIcon
              icon={faUserAstronaut}
              className="text-indigo-600 dark:text-indigo-300 text-sm"
            />
          </div>
          <input
            disabled={sending}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSendMessage();
            }}
            className="flex-1 min-w-0 bg-input-bg h-[44px] text-sm px-4 border border-input-border rounded-lg text-text-primary placeholder:text-placeholder focus:outline-none focus:ring-1 focus:ring-indigo-500/40 focus:border-indigo-400/50"
            placeholder="Type Here..."
          />
          <button
            onClick={handleSendMessage}
            className="h-10 w-10 shrink-0 rounded-lg bg-gradient-to-r from-accent to-accent-bright flex items-center justify-center text-white shadow-lg shadow-indigo-500/25 disabled:opacity-60"
            disabled={sending || loading || !newMessage.trim()}
            aria-label="Send"
          >
            {sending ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faPaperPlane} />
            )}
          </button>
        </div>

        {/*---------------------------------------------------------------------------------- Input Field -------------------------------------------------------------------------------------*/}
      </div>
    </div>
    </ModalPortal>
  );
};

export default NotesModal;
